import React, { useEffect, useRef } from "react";
import { Text, Button, Icon, Container, Heading } from "@telia/teddy";
import _isEmpty from "lodash/isEmpty";
import { useSelector } from "react-redux";
import { FWA_LABELS } from "../../../../../constants";
import PAGES from "../../../../../constants/pages";
import { SummaryTablePdfProps } from "../../components/organisms/SummaryTable/SummaryTablePdf";
import { getOrderRef } from "../../../app/state/selectors";
import { usePrevious } from "../../../checkout/hooks";
import { saveToPdf } from "../../../../utils/saveToPdf";
import { trackFunnelEvent, trackFunnelEventAndNavigate } from "../../../google-analytics";
import SummaryTable from "../../components/organisms/SummaryTable/SummaryTable";
import * as FWA_SELECTORS from "../../state/selectors";
import TEXT from "../../text-consts";
import "./PersonalDetails.less";
import { RootState } from "../../../../../models";

const DownloadCartButton = ({ summaryData }: { summaryData?: SummaryTablePdfProps }) => (
    <Button
        className="download-shoppingcart-button"
        variant="text"
        onClick={() => saveToPdf("fwa-oppsummering", summaryData)}
    >
        <Icon name="document-pdf" size="md" /> Last ned ordreoppsummering
    </Button>
);

const PersonalDetails: React.FC = () => {
    const selectedPackageId = useSelector((state: RootState) => FWA_SELECTORS.getSelectedPackageId(state));
    const selectedPackage = useSelector((state: RootState) => FWA_SELECTORS.getPackageFromId(state, selectedPackageId));
    const isSelectedPackageCabinDIY = useSelector((state: RootState) => FWA_SELECTORS.isSelectedPackageCabinDIY(state));
    const {
        monthlyItems,
        upfrontItems,
        totalMonthlyBBPrice,
        totalMonthlyBBDiscountedPrice,
        totalUpfrontBBPrice,
        totalUpfrontBBDiscountedPrice,
    } = useSelector((state: RootState) => FWA_SELECTORS.getProductsForSelectedPackageId(state, selectedPackageId));
    const successfullySubmittedOrder = useSelector((state: RootState) =>
        FWA_SELECTORS.successfullySubmittedOrder(state),
    );
    const prevSuccessfullySubmittedOrder = usePrevious(successfullySubmittedOrder);
    const orderRef = useSelector((state: RootState) => getOrderRef(state));
    const cartPackage = useSelector((state: RootState) =>
        FWA_SELECTORS.getSelectedPackageDisclaimerText(state, FWA_SELECTORS.getSelectedPackageId(state)),
    );
    const cartRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const asyncHelper = async (): Promise<void> => {
            try {
                await trackFunnelEvent({
                    step: "3",
                    label: FWA_LABELS[2],
                    name: "FWA - Trådløst Bredbånd",
                    isUIEvent: true,
                    uiEventName: "Til oppsummering",
                });
            } finally {
                window.location.assign(`${PAGES.FWA.RECEIPT.URL}${orderRef}/`);
            }
        };
        if (successfullySubmittedOrder && successfullySubmittedOrder !== prevSuccessfullySubmittedOrder) {
            asyncHelper();
        }
    }, [
        successfullySubmittedOrder,
        prevSuccessfullySubmittedOrder,
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
        trackFunnelEventAndNavigate,
        selectedPackage,
        orderRef,
    ]);

    // Trigger GA funnel
    useEffect(() => {
        trackFunnelEvent({
            step: "3",
            label: `${FWA_LABELS[2]}${isSelectedPackageCabinDIY ? " Hytteeier" : ""}`,
            name: "FWA - Trådløst Bredbånd",
        });
        // oxlint-disable-next-line eslint-plugin-react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {(!_isEmpty(monthlyItems) || !_isEmpty(upfrontItems)) && (
                <>
                    <div ref={cartRef} className="fwa-personal-details__summary-container">
                        <Container className="fwa-personal-details__summary">
                            <Heading
                                mb="100"
                                className="fwa-personal-details__summary-heading"
                                as="h2"
                                variant="title-300"
                            >
                                {TEXT.PERSONAL_DETAILS.HEADING_SUMMARY}
                            </Heading>
                            <SummaryTable
                                items={monthlyItems}
                                upfrontItems={upfrontItems}
                                totalUpfrontPrice={totalUpfrontBBPrice}
                                totalUpfrontDiscountedPrice={totalUpfrontBBDiscountedPrice}
                                totalPrice={totalMonthlyBBPrice}
                                totalDiscountedPrice={totalMonthlyBBDiscountedPrice}
                            />
                            {cartPackage && (
                                <Text className="paragraph fwa-summary__disclaimer"> {TEXT.SUMMARY.DISCLAIMER}</Text>
                            )}
                            <div className="fwa-personal-details__download-summary">
                                <DownloadCartButton
                                    summaryData={{
                                        items: monthlyItems,
                                        upfrontItems: upfrontItems,
                                        totalPrice: totalMonthlyBBPrice,
                                        totalDiscountedPrice: totalMonthlyBBDiscountedPrice,
                                        totalUpfrontPrice: totalUpfrontBBPrice,
                                        totalUpfrontDiscountedPrice: totalUpfrontBBDiscountedPrice,
                                    }}
                                />
                            </div>
                        </Container>
                    </div>
                </>
            )}
        </>
    );
};

export default PersonalDetails;
